
import AppHeader from "@/components/AppHeader.vue";
import Error from "@/components/Error.vue";
import ApplicationModal from "@/components/modals/ApplicationModal/ApplicationModal.vue";
import VDModal from "@/components/modals/VDModal";
import Page from "@/components/Page.vue";
import useError from "@/composables/useError";
import usePluralFilter from "@/composables/usePluralFilter";
import { ApplicationType, useGetApplicationsQuery } from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { FilterMatchMode } from "primevue/api";
import { defineComponent, ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import useUser from "@/composables/useUser";

// Query
gql`
  query getApplications {
    applications {
      applicationId
      name
      roles {
        name
      }
    }
  }
`;

export default defineComponent({
  name: "Applications",
  components: { AppHeader, Page, Error, ApplicationModal },
  setup() {
    const ApplicationFilterSessionKey = "applications-filter";

    const applicationModal = ref<VDModal<void> | null>(null);

    const { result, error, loading } = useGetApplicationsQuery();
    const router = useRouter();

    const { user } = useUser();
    const canCreateApp = computed(() => user.value?.isSuperAdmin || false);

    const applications = useResult(result);

    // Initialize filters from query params or sessionStorage
    const initialSearch =
      sessionStorage.getItem(ApplicationFilterSessionKey) || "";
    const filters = useFilters(initialSearch);
    const filteredCount = ref(0);

    function onRowClick(data: ApplicationType) {
      if (data.applicationId && data.applicationId != null)
        router.push({
          name: "application",
          params: {
            applicationId: data.applicationId ?? "",
          },
          query: {
            title: data.name,
          },
        });
    }

    const onFilter = (event: any) => {
      filteredCount.value = event.filteredValue?.length || 0;
    };

    // Watch filter changes and persist to sessionStorage and URL
    watch(
      () => filters.value.global.value,
      (newValue) => {
        const trimmedValue = newValue ? newValue.trim() : "";

        if (trimmedValue === "") {
          sessionStorage.removeItem(ApplicationFilterSessionKey);
        } else {
          sessionStorage.setItem(ApplicationFilterSessionKey, trimmedValue);
        }
      }
    );

    return {
      canCreateApp,
      applicationModal,
      applications,
      error: useError(error),
      loading,
      filters,
      onRowClick,
      onFilter,
      filteredCount,
      usePluralFilter,
    };
  },
});

function useFilters(initialValue = "") {
  return ref({
    global: {
      value: initialValue || null,
      matchMode: FilterMatchMode.CONTAINS,
    },
  });
}
